import type { Message } from '../../types/chat.types';
import type { PageContext } from '../../services/RealChatAgentService';

export interface StarterPrompt {
  text: string;
  icon?: string;
  /** Row id from the starter-prompts API; absent for the built-in defaults. */
  promptId?: string;
  /** generic | bucket | order_status (experiment 1). Absent reads as generic. */
  promptKind?: 'generic' | 'bucket' | 'order_status';
}

/**
 * TRAC-790: the pages where the agent already has real page context and keeps its
 * contextual behavior. Everywhere else gets the connect-to-a-person menu.
 */
/**
 * TRAC-790: how the chat was asked to open, when whoever opened it had an opinion.
 *
 * 'human' starts on the connect-to-a-person menu, 'agent' starts on the agent. Null
 * means nobody said, and the page decides as it always has. Held here rather than
 * threaded through the component tree because the welcome is built in several places
 * and every one of them would otherwise need the same argument passed down to it.
 */
let requestedOpenIntent: 'human' | 'agent' | null = null;
/**
 * TRAC-790: announced, not just stored.
 *
 * The request can land after the opening has already been built — the chat only starts
 * listening once its app has mounted, so an open fired early in the page's life reaches
 * it late. Stored silently, a late request changed nothing and the customer got the
 * page's default anyway. The event lets the chat rebuild an opening nobody has engaged
 * with yet.
 */
export const OPEN_INTENT_EVENT = 'traci-open-intent';
export function setOpenIntent(mode: 'human' | 'agent' | null): void {
  const next = mode === 'human' || mode === 'agent' ? mode : null;
  if (next === requestedOpenIntent) return;
  requestedOpenIntent = next;
  try {
    window.dispatchEvent(new CustomEvent(OPEN_INTENT_EVENT, { detail: { mode: next } }));
  } catch { /* no window (tests) — the value is still set */ }
}
export function openIntent(): 'human' | 'agent' | null {
  return requestedOpenIntent;
}

const TIRE_PAGE_KINDS = new Set(['TireDetail', 'TireSearchResults']);

/**
 * TRAC-790 — the non-tire-page opening, per "The Tire Agent on Every Page".
 *
 * This recreates what LivePerson's menu bot does today: the same four options, the
 * same routing (only "Shopping for new products" goes to Sales; everything else is
 * a service need for Customer Care), and the same second menu behind "My orders".
 *
 * Two deliberate differences from LivePerson's version:
 *  - a clearer opening line ("Before I connect you to an agent, please select one
 *    of the options below" became a greeting that says who we are and what the
 *    options do);
 *  - a third door. Today's menu bot can ONLY hand the customer to a human. Ours
 *    says plainly that it can answer tire questions itself, without burying the
 *    human path the customer came for.
 *
 * Shares of menu clicks over a year of LivePerson conversations, for context:
 * shopping 67%, my orders 23%, hazard claim 7%, rebate 4%.
 */
export const ORDERS_SUBMENU_VALUE = '__menu__:orders';

export const MENU_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Shopping for new products', value: '__route_to__:Sales' },
  { label: 'My orders', value: ORDERS_SUBMENU_VALUE },
  { label: 'Help with a hazard claim', value: '__route_to__:Customer Care' },
  { label: 'Help with a rebate', value: '__route_to__:Customer Care' },
];

/** The second menu behind "My orders" — every branch is Customer Care; the label
 *  travels with the handoff so the rep sees WHY the customer asked for a person. */
export const ORDERS_SUBMENU_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Check order status', value: '__route_to__:Customer Care' },
  { label: 'Process a return', value: '__route_to__:Customer Care' },
  { label: 'Cancel an order', value: '__route_to__:Customer Care' },
  { label: 'Something else', value: '__route_to__:Customer Care' },
];

export const MENU_GREETING = "Hi! Pick one of the options below and I'll connect you with a Tire Rack expert.";
export const MENU_TIRE_DOOR = "Or if you just want to talk tires, that's my specialty — I'm Tire Rack's AI agent, so ask me anything in the chat box below.";
export const ORDERS_SUBMENU_PROMPT = 'Sure — which of these is it?';

function createMenuWelcomeMessage(): Message {
  return {
    id: 'welcome-message',
    role: 'assistant',
    content: '',
    timestamp: new Date(),
    isClientOnly: true,
    kind: 'welcome' as const,
    parts: [
      { type: 'text', content: MENU_GREETING },
      { type: 'routing_actions', actions: MENU_OPTIONS, hint: MENU_TIRE_DOOR },
    ],
  };
}

/**
 * Creates the synthetic welcome message shown on empty state (TRAC-195)
 * This is displayed as the first message when the chat is empty, with
 * instructional text and starter prompts as follow-up actions.
 *
 * @param prompts Array of starter prompts (seasonal + rotating + permanent from admin config)
 * @param pageContext Optional PDP context for contextual greetings (TRAC-291)
 */
export function createWelcomeMessage(
  prompts: StarterPrompt[],
  pageContext?: PageContext | null,
  conditions?: OpeningConditions,
): Message {
  // TRACI-435: an admin has taken the AI out of the chat. Every page opens on the doors
  // to a person, whatever the page or the caller asked for; outside hours there is
  // nobody to connect to and nothing else to offer, so it says when the team is back.
  if (conditions?.livePersonOnly) return livePersonOnlyWelcome(conditions);
  // TRAC-790: doors to a person are only ever shown when there is somebody behind them.
  //
  // Outside live-chat hours every opening falls back to the agent, whatever the page is
  // and whatever was asked for. Four doors that all lead to the same closed sign are
  // four ways to be told no, and the agent can actually help.
  return pageWelcome(prompts, pageContext, !!conditions?.liveChatClosed);
}

/**
 * TRAC-790: what we know about the live-chat lane at the moment the chat opens.
 * Absent means "assume it is open", which is what every caller did before this existed.
 */
export interface OpeningConditions {
  /** Outside live-chat hours — a request for a person cannot be honoured right now. */
  liveChatClosed?: boolean;
  /** LivePerson's own next-open note, e.g. "tomorrow at 8 AM ET". Null when we have none. */
  reopenNote?: string | null;
  /** TRACI-435: the admin "live person only" switch is on; the AI never answers. */
  livePersonOnly?: boolean;
}

/** TRACI-435: the menu's line under the doors, with the AI gone. */
export const LIVE_PERSON_ONLY_HINT = "Or just type what you need below and we'll connect you with a person.";

export const LIVE_PERSON_ONLY_CLOSED = 'Nobody from our team is available right now';

/** TRACI-435: the opening while live-person-only mode is on. */
export function livePersonOnlyWelcome(conditions: OpeningConditions): Message {
  if (conditions.liveChatClosed) {
    // Same words as the backend's answer to anything typed (live_person_only.closed_text).
    const content = conditions.reopenNote
      ? `${LIVE_PERSON_ONLY_CLOSED}; they're back ${conditions.reopenNote}. Please check back then.`
      : `${LIVE_PERSON_ONLY_CLOSED}. Please check back during business hours.`;
    return {
      id: 'welcome-message',
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isClientOnly: true,
      kind: 'welcome' as const,
      parts: [{ type: 'text', content }],
    };
  }
  return {
    id: 'welcome-message',
    role: 'assistant',
    content: '',
    timestamp: new Date(),
    isClientOnly: true,
    kind: 'welcome' as const,
    parts: [
      { type: 'text', content: MENU_GREETING },
      { type: 'routing_actions', actions: MENU_OPTIONS, hint: LIVE_PERSON_ONLY_HINT },
    ],
  };
}

/**
 * TRAC-790: does this opening offer doors to a person?
 *
 * The chat asks before it draws, because an opening that offers people must not be
 * drawn until we know whether any are there — otherwise it flips from red to blue in
 * front of the customer. An opening that offers the agent needs no such wait.
 */
export function offersPeople(pageContext?: PageContext | null): boolean {
  if (openIntent() === 'human') return true;
  if (openIntent() === 'agent') return false;
  return !!pageContext?.page_kind && !TIRE_PAGE_KINDS.has(pageContext.page_kind);
}

/** TRACI-910: which experience the shopper got first, for the staff live chat page. */
export type OpeningKind = 'liveperson_first' | 'ai_first';

/**
 * TRACI-910: the opening a drawn welcome gave the shopper. Doors to a person (the
 * human-first menu, either live-person-only opening) is LivePerson first; the agent's
 * greeting is AI first. Null when no welcome was drawn. "LivePerson first" means doors
 * were offered, even if the shopper then typed to the AI instead.
 */
export function openingKind(welcome: Message | null | undefined): OpeningKind | null {
  if (!welcome) return null;
  // Read off the drawn welcome only (never the live availability, which can change after the
  // welcome is drawn). The closed live-person-only opening has no doors, only its closed note.
  const parts = welcome.parts || [];
  const doors = parts.some((p) => p.type === 'routing_actions');
  const closedLivePersonOnly = parts.some(
    (p) => p.type === 'text' && typeof p.content === 'string' && p.content.startsWith(LIVE_PERSON_ONLY_CLOSED),
  );
  return doors || closedLivePersonOnly ? 'liveperson_first' : 'ai_first';
}

/**
 * TRACI-432: is live chat closed, from the Live Chat button's availability state?
 *
 * Only a settled "open" offers people. "checking" is not closed: it is not known yet,
 * and an opening that offers people waits for it (see openingUndecided). Every other
 * state (closed_hours, closed_no_agents, anything new) is closed, so the chat opens
 * agent-first rather than offering doors that may lead nowhere.
 */
export function liveChatClosedFrom(availState: string): boolean {
  return availState !== 'open' && availState !== 'checking';
}

/**
 * TRACI-432: should the opening wait, under the neutral "finding out" header?
 *
 * Only an opening that would offer people waits, and only while availability is still
 * being checked: drawing it first would flip it from red to blue in front of the
 * customer when the answer comes back closed.
 */
export function openingUndecided(availState: string, pageContext?: PageContext | null): boolean {
  return availState === 'checking' && offersPeople(pageContext);
}

function pageWelcome(
  prompts: StarterPrompt[],
  pageContext: PageContext | null | undefined,
  closed: boolean,
): Message {
  // TRAC-790: a request for a person is answered FIRST, before any page-based greeting.
  //
  // This sat further down and was unreachable in the case it exists for: on a tire page
  // the contextual greeting returns above it, so somebody pressing "Chat with an Expert"
  // on a product page asked for a human and got a tire recommendation. The one page
  // where the override matters most was the one page it did nothing on.
  if (openIntent() === 'human' && !closed) return createMenuWelcomeMessage();

  // TRAC-291: If we have PDP context, generate a contextual greeting
  // TRAC-454: Use the same prompts from admin settings (fetched by page_name)
  if (pageContext && pageContext.tire_name) {
    return createContextualWelcomeMessage(pageContext, prompts);
  }

  // TRAC-790: a tire search-results page has no single tire to greet about, so it
  // greets about the SEARCH — the vehicle and/or size the customer is shopping. It
  // previously fell into the tire greeting only because a "tire name" was being
  // scraped off the page title ("2008 Toyota Sienna LE FWD Tires"); once we stopped
  // inventing tires from titles, results pages had nothing left and dropped to the
  // generic welcome. This is the greeting they should have had all along.
  if (pageContext?.page_kind === 'TireSearchResults') {
    const resultsWelcome = createResultsWelcomeMessage(pageContext, prompts);
    if (resultsWelcome) return resultsWelcome;
  }

  // TRAC-790: whoever opened the chat gets the last word on how it opens.
  //
  // The page decides by default, and that default is right: a tire page opens into the
  // agent with that tire in hand, everything else opens the connect-to-a-person menu.
  // But the page is the wrong owner when two buttons on the SAME page want opposite
  // things. Our corner bubble is an offer of the agent; the site's own "Chat with an
  // Expert" is somebody asking for a person. The URL is identical for both, so only the
  // caller can tell them apart.
  if (openIntent() !== 'agent' && !closed) {
    // Nobody asked, so the page decides, exactly as before. Only when we KNOW the page
    // kind — an unlabelled page (an older embed with no page signal) keeps the generic
    // starter-prompt welcome. Suppressed outside hours for the same reason as above:
    // there is nobody behind these doors.
    if (pageContext?.page_kind && !TIRE_PAGE_KINDS.has(pageContext.page_kind)) {
      return createMenuWelcomeMessage();
    }
  }

  return {
    id: 'welcome-message',
    role: 'assistant',
    content: '',
    timestamp: new Date(),
    isClientOnly: true,
    // TRAC-637: tag with the kind discriminator so effectiveKind() resolves
    // correctly in ChatInterface (replaces the legacy id-based check).
    kind: 'welcome' as const,
    parts: [
      {
        type: 'text',
        content: "**Share your vehicle + driving style.** I'll point you to the best tire matches and comparisons.",
      },
      {
        type: 'text',
        content: "If you don't know where to start, try one of the options below:",
      },
      {
        type: 'follow_up_actions',
        actions: prompts.map(prompt => ({
          label: prompt.text,
          value: prompt.text,
        })),
      },
    ],
  };
}

/**
 * TRAC-291: Build a contextual greeting + starter prompts based on PDP context.
 *
 * Scenarios:
 * 1. Tire only → confirm tire, offer general help
 * 2. Tire + size → confirm tire & size
 * 3. Tire + vehicle → confirm tire & vehicle
 * 4. Tire + size + vehicle → confirm all
 */
/** Bolds everything before the first em-dash or sentence break in a greeting. */
function boldFirstClause(text: string): string {
  const emDash = text.indexOf(' — ');
  if (emDash !== -1) {
    return `**${text.slice(0, emDash)}** — ${text.slice(emDash + 3)}`;
  }
  const period = text.indexOf('. ');
  if (period !== -1) {
    return `**${text.slice(0, period + 1)}** ${text.slice(period + 2)}`;
  }
  return `**${text}**`;
}

/**
 * TRAC-790: the opening line for a tire search-results page.
 *
 * Returns null when the page gave us neither a vehicle nor a size — with nothing
 * true to say about the search, the generic welcome is the honest fallback rather
 * than a greeting that pretends to know something.
 */
function createResultsWelcomeMessage(ctx: PageContext, prompts: StarterPrompt[]): Message | null {
  const vehicle = [ctx.vehicle_year, ctx.vehicle_make, ctx.vehicle_model, ctx.vehicle_trim]
    .filter(Boolean)
    .join(' ');
  const size = ctx.search_size;

  let greeting: string;
  if (vehicle && size) {
    greeting = `I see you're looking at tires for your ${vehicle} in ${size} — happy to help you narrow it down.`;
  } else if (vehicle) {
    greeting = `I see you're looking at tires for your ${vehicle} — happy to help you narrow it down.`;
  } else if (size) {
    greeting = `I see you're browsing ${size} tires — happy to help you narrow it down.`;
  } else {
    return null;
  }

  return {
    id: 'welcome-message',
    role: 'assistant',
    content: '',
    timestamp: new Date(),
    isClientOnly: true,
    kind: 'welcome' as const,
    parts: [
      { type: 'text', content: boldFirstClause(greeting) },
      {
        type: 'follow_up_actions',
        actions: prompts.map((prompt) => ({ label: prompt.text, value: prompt.text })),
      },
    ],
  };
}

function createContextualWelcomeMessage(ctx: PageContext, prompts: StarterPrompt[]): Message {
  const tireName = ctx.tire_name!;
  const hasSize = !!ctx.tire_size;
  const hasVehicle = !!ctx.vehicle_year && !!ctx.vehicle_make && !!ctx.vehicle_model;

  // Build vehicle string
  const vehicleStr = hasVehicle
    ? [ctx.vehicle_year, ctx.vehicle_make, ctx.vehicle_model, ctx.vehicle_trim]
        .filter(Boolean)
        .join(' ')
    : '';

  // Build greeting text
  let greeting: string;
  if (hasSize && hasVehicle) {
    greeting = `I see you're looking at the ${tireName} in ${ctx.tire_size} for your ${vehicleStr}. Happy to help with anything!`;
  } else if (hasVehicle) {
    greeting = `I see you're looking at the ${tireName} for your ${vehicleStr}. Happy to help with questions or find the right fit!`;
  } else if (hasSize) {
    greeting = `I see you're looking at the ${tireName} in ${ctx.tire_size}. Happy to answer any questions or help you find the right fit!`;
  } else {
    greeting = `I see you're checking out the ${tireName} — happy to help with any questions about this tire or anything else!`;
  }

  return {
    id: 'welcome-message',
    role: 'assistant',
    content: '',
    timestamp: new Date(),
    isClientOnly: true,
    // TRAC-637: tag with the kind discriminator so effectiveKind() resolves
    // correctly in ChatInterface (replaces the legacy id-based check).
    kind: 'welcome' as const,
    parts: [
      {
        type: 'text',
        content: boldFirstClause(greeting),
      },
      {
        type: 'follow_up_actions',
        actions: prompts.map(prompt => ({
          label: prompt.text,
          value: prompt.text,
        })),
      },
    ],
  };
}
